import { Injectable } from "@angular/core";
import { environment } from "../../environments/environment";
import { Determinant } from "../models/determinant";
import { BehaviorSubject } from "rxjs";
import { RecoverOrphanConfig } from "../models/recover-orphan-config";

@Injectable()
export class Store {

  public debugMode$ = new BehaviorSubject<boolean>(localStorage.getItem("dryRunDebugMode") === "true");
  public formBugsDrawer$ = new BehaviorSubject<{ errors: RecoverOrphanConfig[]; formName: string } | null>(null);

  // note: underscore ( _ ) as prefix to value name means that you should not change value of the field directly
  // instead of that try change value with the use of setters

  public state = {
    common: {
      get helpToggleEnabled() {
        return false;
        // if (!localStorage.getItem("helpToggleEnabled")) {
        //   localStorage.setItem("helpToggleEnabled", "true");
        // }
        // return localStorage.getItem("helpToggleEnabled") === "true";
      },
      set helpToggleEnabled(toggle: boolean) {
        localStorage.setItem("helpToggleEnabled", toggle.toString());
      },
      userLevels: {
        pages: {} as any,
        set: (page, value) => {
          localStorage.setItem("UserLevel-" + page, JSON.stringify(value));
        },
        get: (page) => {
          if (localStorage.getItem("UserLevel-" + page)) {
            return JSON.parse(localStorage.getItem("UserLevel-" + page));
          } else {
            return false;
          }
        },
        reset: (page) => {
          this.state.common.userLevels.pages[page] = 1;
        },
        activePage: undefined,
      },
      botServicesBoards: {
        servicesBots: {} as any,
        set: (service, bot, ws, board, value) => {
          localStorage.setItem("DataMappingBoard-" + service + "-" + bot + "-" + ws + "-" + board, JSON.stringify(value));
        },
        get: (service, bot, ws, board) => {
          if (localStorage.getItem("DataMappingBoard-" + service + "-" + bot + "-" + ws + "-" + board)) {
            return JSON.parse(localStorage.getItem("DataMappingBoard-" + service + "-" + bot + "-" + ws + "-" + board));
          } else {
            return -1;
          }
        },
      },
      applicantFormPage: {
        set: (page) => {
          localStorage.setItem("applicantFormPage", page);
        },
        get: () => {
          if (localStorage.getItem("applicantFormPage")) {
            return localStorage.getItem("applicantFormPage");
          } else {
            return false;
          }
        },
      },
      serviceId: undefined,
      defaultMyAccountServiceId: null,
      serviceName: null,
      dryRunDrawerOpen: false,
      dryRunDebugMode: false,
      determinants: undefined,
      conditions: undefined,
      addDeterminantModal: {
        _show: false,
        showModal: () => {
          this.state.common.addDeterminantModal._show = true;
          Store.callUglyAngularRedraw();
        },
        onClose: (fn) => {
          this.state.common.addDeterminantModal.onCloseListeners.push(fn);
        },
        onCloseListeners: [],
        closeModal: (newDeterminant: Determinant) => {
          this.state.common.addDeterminantModal._show = false;
          this.state.common.addDeterminantModal.onCloseListeners.forEach((it) => it(newDeterminant));
        },
      },
      editDeterminantModal: {
        _show: false,
        _determinant: undefined,
        showModal: (det) => {
          this.state.common.editDeterminantModal._determinant = det;
          this.state.common.editDeterminantModal._show = true;
          Store.callUglyAngularRedraw();
        },
        onClose: (fn) => {
          this.state.common.editDeterminantModal.onCloseListeners.push(fn);
        },
        onCloseListeners: [],
        closeModal: () => {
          this.state.common.editDeterminantModal._show = false;
          this.state.common.editDeterminantModal.onCloseListeners.forEach((it) => it());
        },
        onDeterminantChangeListeners: [],
        onDeterminantHasChanged(fn) {
          this.onDeterminantChangeListeners.push(fn);
        },
        emitDeterminantHasChanged(determinant) {
          this.onDeterminantChangeListeners.forEach((it) => it(determinant));
        },
      },
      determinantList: {
        onDeterminantChangeListeners: [],
        onDeterminantHasChanged(fn) {
          this.onDeterminantChangeListeners.push(fn);
        },
        emitDeterminantHasChanged(updatedLogicCondition) {
          this.onDeterminantChangeListeners.forEach((it) => it(updatedLogicCondition));
        },
      },
      isEditOrNewDeterminantWindowOpened: () => {
        return this.state.common.addDeterminantModal._show || this.state.common.editDeterminantModal._show;
      },
      sections: {
        setTab(tab) {
          localStorage.setItem("sectionsActiveTab", tab);
        },
        getTab() {
          const tab = localStorage.getItem("sectionsActiveTab");
          return tab ? tab : "general";
        },
        active: false,
        type: null,
        reset: () => {
          this.state.common.sections.active = false;
          this.state.common.sections.type = null;
        },
      },
      determinantSection: {
        topLayer: null,
        determinant: {},
        reset: () => {
          this.state.common.determinantSection.topLayer = null;
          this.state.common.determinantSection.determinant = {};
        },
      },
      runningPublications: 0,
      sidemenuSmall: false,
      topBarCollapsed: true,
      lockedPageByUser: false,
      lockPublishButton: false,
      get lang() {
        return localStorage.getItem("lang") || environment.DEFAULT_LANGUAGE;
      },
      set lang(lang: string) {
        localStorage.setItem("lang", lang);
      },
      get debugMode() {
        return localStorage.getItem("dryRunDebugMode") === "true";
      },
      set debugMode(value: boolean) {
        localStorage.setItem("dryRunDebugMode", value.toString());
      },
      dryRunResults: {
        get: (serviceId: string) => {
          const stored = localStorage.getItem(`dryRunResults-${serviceId}`);
          return stored ? JSON.parse(stored) : null;
        },
        set: (serviceId: string, value: any) => {
          localStorage.setItem(`dryRunResults-${serviceId}`, JSON.stringify(value));
        }
      },
      currentlyPublishingUsers: "",
    },
  };

  public static callUglyAngularRedraw() {
    // I have no idea why Angular requires click or any mouse interaction to redraw
    const nav = document.getElementsByTagName("nav");
    if (nav && nav[0]) {
      setTimeout(() => {
        nav[0].click();
      }, 100);
      setTimeout(() => {
        nav[0].click();
      }, 200);
      setTimeout(() => {
        nav[0].click();
      }, 500);
      setTimeout(() => {
        nav[0].click();
      }, 1000);
      setTimeout(() => {
        nav[0].click();
      }, 2000);
      setTimeout(() => {
        nav[0].click();
      }, 3000);
    }
  }
}
